/**
 * Gemeinsames Icon-Set — 1:1 aus dem Design-Canvas-Artefakt "ShiftSync
 * Rollenansichten" übernommen (Main.dc.html, Filialleitung.dc.html,
 * StandortDetail.dc.html). Die App hat keine Icon-Bibliothek, daher
 * handgerollte Inline-SVG-Komponenten statt einer neuen Abhängigkeit.
 * Ursprünglich lokal in DashboardPage.tsx, ausgelagert (Nachtrag
 * Standort-Detail-Ansicht, 08.09.2026), damit beide Seiten dieselben
 * Icon-Pfade nutzen statt sie zweimal zu pflegen.
 */

import type { SVGProps } from 'react';

export function IconBase({ children, ...props }: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      {children}
    </svg>
  );
}

export function IconChevronRight(props: SVGProps<SVGSVGElement>) {
  return (
    <IconBase strokeWidth={2} {...props}>
      <polyline points="9 6 15 12 9 18" />
    </IconBase>
  );
}

export function IconChevronLeft(props: SVGProps<SVGSVGElement>) {
  return (
    <IconBase strokeWidth={2} {...props}>
      <polyline points="15 6 9 12 15 18" />
    </IconBase>
  );
}

export function IconChevronDown(props: SVGProps<SVGSVGElement>) {
  return (
    <IconBase strokeWidth={2} {...props}>
      <polyline points="6 9 12 15 18 9" />
    </IconBase>
  );
}

export function IconPlus(props: SVGProps<SVGSVGElement>) {
  return (
    <IconBase strokeWidth={2.2} {...props}>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </IconBase>
  );
}

export function IconPeople(props: SVGProps<SVGSVGElement>) {
  return (
    <IconBase strokeWidth={1.75} {...props}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </IconBase>
  );
}

export function IconClock(props: SVGProps<SVGSVGElement>) {
  return (
    <IconBase strokeWidth={1.75} {...props}>
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 15 15" />
    </IconBase>
  );
}

export function IconLock(props: SVGProps<SVGSVGElement>) {
  return (
    <IconBase strokeWidth={1.75} {...props}>
      <rect x="3" y="11" width="18" height="10" rx="1" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </IconBase>
  );
}

export function IconUnlock(props: SVGProps<SVGSVGElement>) {
  return (
    <IconBase strokeWidth={2} {...props}>
      <rect x="3" y="11" width="18" height="10" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 9.9-1" />
    </IconBase>
  );
}

export function IconCheck(props: SVGProps<SVGSVGElement>) {
  return (
    <IconBase strokeWidth={2} {...props}>
      <polyline points="20 6 9 17 4 12" />
    </IconBase>
  );
}

export function IconCard(props: SVGProps<SVGSVGElement>) {
  return (
    <IconBase strokeWidth={1.75} {...props}>
      <rect x="2" y="6" width="20" height="12" rx="2" />
      <path d="M12 12h.01" />
      <path d="M6 12h.01" />
      <path d="M18 12h.01" />
    </IconBase>
  );
}

export function IconUser(props: SVGProps<SVGSVGElement>) {
  return (
    <IconBase strokeWidth={1.75} {...props}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21v-1a8 8 0 0 1 16 0v1" />
    </IconBase>
  );
}

export function IconCalendar(props: SVGProps<SVGSVGElement>) {
  return (
    <IconBase strokeWidth={1.75} {...props}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </IconBase>
  );
}

/** "Vorlagen verwalten" — Kreis mit Kreuz, aus StandortDetail.dc.html. */
export function IconTemplates(props: SVGProps<SVGSVGElement>) {
  return (
    <IconBase strokeWidth={1.75} {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12h8M12 8v8" />
    </IconBase>
  );
}

/** Warndreieck ("Unbestätigte Einsprünge"), aus StandortDetail.dc.html. */
export function IconAlertTriangle(props: SVGProps<SVGSVGElement>) {
  return (
    <IconBase strokeWidth={1.75} {...props}>
      <path d="M12 9v4" />
      <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
      <path d="M12 17h.01" />
    </IconBase>
  );
}
